import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import Notification from '@rio-cloud/rio-uikit/Notification';
import type { SelectOption } from '@rio-cloud/rio-uikit/Select';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { z } from 'zod';

import { useUpdateCard } from '@/api/cards';
import type { RfidCardType } from '@/api/schema';
import Alert from '@/components/ui/Alert';
import Input from '@/components/ui/Input';
import SelectInput from '@/components/ui/SelectInput';
import { createDebugConfig, trackCustomEvent } from '@/configuration/setup/googleAnalytics';
import { t } from '@/lib/utils';
import { useUserStore } from '@/stores/userStore';

export type CardDetailsProps = {
    onClose: () => void;
    setAbort?: (state: boolean) => void;
    onSubmit: () => void;
    onDirtyChange: (state: boolean) => void;
    state?: RfidCardType;
};

const RfidCardForm = z.object({
    serial: z.string(),
    number: z.string(),
    isActive: z.string(),
    alias: z.string().optional(),
});
type RfidCardFormType = z.infer<typeof RfidCardForm>;

const CardDetails = ({ onClose, onSubmit, setAbort, state, onDirtyChange }: CardDetailsProps) => {
    const { account } = useUserStore();
    const updateCardMutation = useUpdateCard();
    const debugConfig = createDebugConfig();

    const permissions = account?.permissions;
    const hasPermissions = permissions?.includes('emsp-cards.write');

    const form = useForm<RfidCardFormType>({
        defaultValues: {
            ...state,
            alias: state?.alias ?? '',
            isActive: state?.isActive ? 'active' : 'inactive',
        },
        mode: 'all',
        resolver: zodResolver(RfidCardForm),
    });

    useEffect(() => {
        if (state) {
            form.reset({
                ...state,
                alias: state.alias ?? '',
                isActive: state.isActive ? 'active' : 'inactive',
            });
        }
    }, [state, form]);

    const { dirtyFields } = form.formState;
    const isDirty = dirtyFields.alias || dirtyFields.isActive;

    // A status-change info alert is shown when the user switches the card status:
    // - inactive -> active: activation may take up to 48 hours to take effect.
    // - active -> inactive: deactivation may take up to 48 hours and any charging
    //   during this time will still be billed.
    const selectedStatus = form.watch('isActive');
    const statusChangeInfoId =
        state?.isActive === false && selectedStatus === 'active'
            ? 'chargingCards.activationInfo.description'
            : state?.isActive === true && selectedStatus === 'inactive'
              ? 'chargingCards.deactivationInfo.description'
              : undefined;

    useEffect(() => {
        if (isDirty !== undefined) {
            onDirtyChange?.(isDirty);
        }
    }, [isDirty, onDirtyChange]);

    const handleSubmitForm = async (values: RfidCardFormType) => {
        const isActive = values.isActive === 'active';
        try {
            await updateCardMutation.mutateAsync({
                serial: values.serial,
                alias: values.alias,
                isActive: isActive,
                hasStatusChanged: state?.isActive !== isActive,
            });

            trackCustomEvent(
                'charging_card_change',
                {
                    trigger: 'click',
                    flow_name: 'charging_cards_management',
                    status: values.isActive,
                    alias: values.alias,
                    update_successful: true,
                },
                debugConfig
            );

            onSubmit();

            Notification.success(<FormattedMessage id='general.saveChanges.successMessage' />);
        } catch {
            Notification.error(<FormattedMessage id='general.saveChanges.errorMessage' />);
        }
    };

    const handleOnClose = useCallback(() => {
        if (isDirty && setAbort) {
            setAbort(true);
        } else {
            onClose();
        }
    }, [isDirty, onClose, setAbort]);

    const activeText = t('general.active');
    const inactiveText = t('general.inactive');
    const selectOptions = useMemo<SelectOption[]>(
        () => [
            {
                id: 'active',
                label: (
                    <div className='display-flex gap-5'>
                        <span className='rioglyph text-size-h5 rioglyph-ok-sign text-color-success' />
                        <span>{activeText}</span>
                    </div>
                ),
            },
            {
                id: 'inactive',
                label: (
                    <div className='display-flex gap-5'>
                        <span className='rioglyph text-size-h5 rioglyph-remove-sign text-color-dark' />
                        <span>{inactiveText}</span>
                    </div>
                ),
            },
        ],
        []
    );

    return (
        <div>
            <div className='display-flex flex-column height-100pct justify-content-between'>
                <div className='display-flex flex-column margin-bottom-25 gap-10'>
                    {statusChangeInfoId && (
                        <Alert
                            content={
                                <p className='margin-y-0'>
                                    <FormattedMessage
                                        id={statusChangeInfoId}
                                        values={{ b: chunks => <b>{chunks}</b> }}
                                    />
                                </p>
                            }
                            className='padding-15'
                        />
                    )}
                    <Controller
                        name='isActive'
                        control={form.control}
                        render={({ field: { onChange, value } }) => (
                            <SelectInput
                                label={t('chargingCards.rfidCardStatus')}
                                selectAttributes={{
                                    onChange: (item?: SelectOption) => onChange(item?.id),
                                    options: selectOptions.map(opt => ({ ...opt, selected: opt.id === value })),
                                    disabled: !hasPermissions,
                                }}
                            />
                        )}
                    />
                    <Controller
                        name='alias'
                        control={form.control}
                        disabled={!hasPermissions}
                        render={({ field }) => (
                            <>
                                <Input
                                    label={t('chargingCards.cardAlias')}
                                    className='margin-bottom-0'
                                    inputAttributes={field}
                                />
                                <p className='margin-0 text-size-12'>{t('chargingCards.cardAliasDescription')}</p>
                            </>
                        )}
                    />
                </div>

                {hasPermissions && (
                    <div className='btn-toolbar justify-content-end'>
                        <Button
                            disabled={!isDirty || updateCardMutation.isLoading}
                            onClick={handleOnClose}
                            aria-disabled={!isDirty}
                        >
                            {t('general.button.cancel')}
                        </Button>
                        <Button
                            className={updateCardMutation.isLoading ? 'btn-loading' : ''}
                            onClick={form.handleSubmit(handleSubmitForm)}
                            disabled={!isDirty || updateCardMutation.isLoading}
                            aria-disabled={!isDirty}
                            bsStyle='primary'
                        >
                            {t('general.button.saveChanges')}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CardDetails;
